import { format } from 'date-fns';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { FC, useMemo, useRef, useState } from 'react';

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Avatar,
	Badge,
	Box,
	Button,
	CalendarOutline,
	CheckOutline,
	DotsHorizontalOutline,
	HStack,
	IconButton,
	LinkAltOutline,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PaperClipOutline,
	PencilOutline,
	Portal,
	Skeleton,
	StyleProps,
	Text,
	Textarea,
	Tooltip,
	TrashOutline,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
	Wrap,
	XOutline,
} from '@bonusly/design-system';

import { Activity, Goal, GoalStatus } from '../../../../types/goal';
import { useAgendaTopicAssociated, useAgendaTopicFromEntity } from '../../hooks/use-agenda-topic-entity';
import { useAddGoalUpdate, useDeleteGoal, useGoal, useUpdateGoal } from '../../hooks/use-goals';
import { GoalUpdateCard } from '../activity-cards';
import { BonusCardV2 } from '../activity-cards/bonus-card-v2';
import { FeedbackCardV2 } from '../activity-cards/feedback-card-v2';
import { TopicHistoryCard } from '../activity-cards/topic-history-card';
import { AddActivityToGoalModal } from './add-activity-to-goal-modal';
import { EditGoalModal } from './edit-goal-modal';
import { GoalCollaboratorManager } from './goal-collaborator-manager';
import { GoalCompletedModal } from './goal-completed-modal';
import { GoalInitiativeCard } from './goal-initiative-card';
import { GoalStatusBadge } from './goal-status-badge';

const MotionBox = motion.create(Box);

interface GoalDetailsPanelProps extends StyleProps {
	goal: Goal | null;
	isOpen: boolean;
	onClose: () => void;
	readonly?: boolean;
	inDrawer?: boolean;
	history?: any[];
}

interface GoalDetailsContentProps {
	goal: Goal;
	readonly: boolean;
	updateText: string;
	setUpdateText: (text: string) => void;
	isSubmitting: boolean;
	onSubmitUpdate: () => void;
	onStatusChange: (status: GoalStatus) => void;
	onDiscussInOneOnOne: () => void;
	onCompleteGoal: () => void;
	onEditGoal: () => void;
	onDeleteGoal: () => void;
	onAddActivity: () => void;
	topicAssociation: any;
	isAddingToOneOnOne: boolean;
	isLoadingAssociation: boolean;
	groupedActivities: Array<{ date: string; activities: Activity[] }>;
	borderColor: any;
	parentContainer?: 'OneOnOneDrawer' | 'default';
	isHistoryLoading?: boolean;
	toast: ReturnType<typeof useToast>;
}

const GoalDetailsContent: FC<GoalDetailsContentProps> = ({
	goal,
	readonly,
	updateText,
	setUpdateText,
	isSubmitting,
	onSubmitUpdate,
	onStatusChange,
	onDiscussInOneOnOne,
	onCompleteGoal,
	onEditGoal,
	onDeleteGoal,
	onAddActivity,
	topicAssociation,
	isAddingToOneOnOne,
	isLoadingAssociation,
	groupedActivities,
	borderColor,
	parentContainer = 'default',
	isHistoryLoading = false,
	toast,
}) => {
	const maxAvatars = 5;
	const visibleCollaborators = goal.collaborators?.slice(0, maxAvatars) || [];
	const remainingCount = Math.max(0, (goal.collaborators?.length || 0) - maxAvatars);

	const handleAvatarClick = (collaboratorId: string) => {
		console.log('Open profile:', collaboratorId);
	};

	const timelineEntryVariants: Variants = {
		hidden: { opacity: 0, y: 20, scale: 0.95 },
		visible: {
			opacity: 1,
			y: 0,
			scale: 1,
			transition: {
				type: 'spring',
				stiffness: 300,
				damping: 25,
				duration: 0.5,
			},
		},
	};

	const avatarHoverVariants: Variants = {
		hover: {
			scale: 1.1,
			y: -2,
			transition: {
				type: 'spring',
				stiffness: 400,
				damping: 20,
			},
		},
	};

	return (
		<VStack spacing={4} align="stretch" data-component="goal-details-panel-content">
			{/* Goal Header */}
			<VStack align="start" spacing={4}>
				{/* Status and Menu */}
				<HStack justify="space-between" w="full" align="start">
					<GoalStatusBadge status={goal.status} toggleable={!readonly && !!goal.abilities?.canModify} onStatusChange={onStatusChange} />

					{!readonly && (goal.abilities?.canModify || goal.abilities?.canDelete) && (
						<Menu>
							<MenuButton
								as={IconButton}
								aria-label="Goal options"
								icon={<DotsHorizontalOutline />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ color: 'gray.600', bg: 'gray.100' }}
							/>
							<MenuList zIndex={1002}>
								<MenuItem
									icon={<LinkAltOutline />}
									onClick={() => {
										const shareUrl = `${window.location.origin}/growth/goals/${goal.id}`;
										navigator.clipboard.writeText(shareUrl);
										toast({
											title: 'Goal link copied!',
											description: 'Share this link to open the goal directly.',
											status: 'success',
											duration: 3000,
										});
									}}
								>
									Share goal
								</MenuItem>
								{goal.abilities?.canModify && (
									<MenuItem icon={<PencilOutline />} onClick={onEditGoal}>
										Edit goal
									</MenuItem>
								)}
								{goal.abilities?.canDelete && (
									<MenuItem icon={<TrashOutline />} onClick={onDeleteGoal}>
										Delete goal
									</MenuItem>
								)}
							</MenuList>
						</Menu>
					)}
				</HStack>

				{/* Title and Description */}
				<VStack align="start" spacing={2} w="full">
					<Text color="text.default" fontSize={200} fontWeight="semibold" lineHeight="shorter">
						{goal.title}
					</Text>
					{goal.summary && (
						<Text color="text.soft" fontSize={50} lineHeight="relaxed" whiteSpace="pre-line">
							{goal.summary}
						</Text>
					)}
				</VStack>

				{/* Metadata */}
				<VStack align="start" spacing={4} w="full">
					{/* Date Range */}
					{(goal.startDate || goal.endDate) && (
						<HStack spacing={2} color="text.default" fontSize="sm">
							<CalendarOutline size="sm" />
							<Text fontWeight="medium" fontSize="sm" color="text.default">
								{!goal.endDate && 'Start: '}
								{goal.startDate && format(new Date(goal.startDate), 'MMM d, yyyy')}
								{goal.startDate && goal.endDate && ' - '}
								{!goal.startDate && 'Target: '}
								{goal.endDate && format(new Date(goal.endDate), 'MMM d, yyyy')}
							</Text>
						</HStack>
					)}

					{/* Collaborators with Avatar Group */}
					{goal.collaborators && goal.collaborators.length > 0 && (
						<VStack spacing={2} align="flex-start">
							<Text fontSize="sm" color="text.default" fontWeight="medium">
								Collaborators
							</Text>
							<HStack spacing={2}>
								{visibleCollaborators.map((collaborator) => (
									<MotionBox key={collaborator.id} variants={avatarHoverVariants} whileHover="hover">
										<Tooltip label={`${collaborator.displayName}`} placement="top">
											<Avatar
												src={collaborator.profilePicUrl}
												name={collaborator.displayName}
												size="md"
												border="2px solid white"
												cursor="pointer"
												onClick={() => handleAvatarClick(collaborator.id)}
											/>
										</Tooltip>
									</MotionBox>
								))}
								{remainingCount > 0 && (
									<Box
										w="32px"
										h="32px"
										borderRadius="full"
										bg="gray.100"
										border="2px solid white"
										display="flex"
										alignItems="center"
										justifyContent="center"
										fontSize="xs"
										fontWeight="medium"
										color="gray.600"
									>
										+{remainingCount}
									</Box>
								)}
							</HStack>
						</VStack>
					)}
				</VStack>
			</VStack>

			{/* Initiative */}
			{goal?.initiative && (
				<VStack align="start" spacing={2} w="full">
					<Text fontSize="sm" color="text.default" fontWeight="medium">
						Supporting initiative
					</Text>
					<GoalInitiativeCard initiative={goal.initiative} />
				</VStack>
			)}

			{/* Tagged values */}
			{goal?.tags && goal.tags.length > 0 && (
				<VStack align="start" spacing={2} w="full">
					<Text fontSize="sm" color="text.default" fontWeight="medium">
						Tagged values
					</Text>
					<Wrap>
						{goal.tags.map((tag) => (
							<Badge
								key={tag}
								bg="hashtag.bgAlpha"
								color="text.hashtag"
								borderRadius="full"
								textTransform="none"
								fontSize="sm"
								fontWeight="normal"
								px={3}
								py={1}
							>
								{tag}
							</Badge>
						))}
					</Wrap>
				</VStack>
			)}

			{/* Action Buttons */}
			{!readonly && (
				<Box pb={6} borderBottom="1px solid" borderColor={borderColor}>
					<HStack spacing={3} w="full">
						{/* Only show "Discuss in 1:1" button if not in OneOnOneDrawer */}
						{parentContainer !== 'OneOnOneDrawer' && (
							<Button
								variant="alpha"
								colorScheme="secondary"
								size="sm"
								flex={1}
								onClick={onDiscussInOneOnOne}
								isDisabled={topicAssociation?.associated || isAddingToOneOnOne || isLoadingAssociation}
								isLoading={isAddingToOneOnOne}
								loadingText="Adding..."
								leftIcon={topicAssociation?.associated ? <CheckOutline color="points.fgOnSurface" /> : undefined}
							>
								{topicAssociation?.associated ? 'In 1:1' : 'Discuss in 1:1'}
							</Button>
						)}

						{/*
							only show the 'complete goal' button when it's still incomplete
							and the user has the ability to modify the goal
						*/}
						{goal?.status !== 'completed' && goal.abilities?.canModify && (
							<Button variant="alpha" colorScheme="secondary" size="sm" flex={1} onClick={onCompleteGoal}>
								Complete goal
							</Button>
						)}
					</HStack>
				</Box>
			)}

			{/* Goal History */}
			<VStack id="goal-details-content" py={0} spacing={2} w="full" align="stretch">
				<Text fontSize="lg" fontWeight="semibold" color="text.default">
					Goal History
				</Text>

				{/* Update Input Section */}
				{!readonly && goal.abilities?.canModify && (
					<VStack spacing={2} align="stretch">
						<Box position="relative">
							<Textarea
								border="1px solid"
								borderColor="border.input"
								borderRadius="lg"
								fontSize="md"
								onChange={(e) => setUpdateText(e.target.value)}
								placeholder="Add an update..."
								px={3}
								py={2}
								rows={3}
								value={updateText}
								_focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
							/>
							<HStack position="absolute" bottom={3} right={3} spacing={2}>
								{/* TODO: Add voice input */}
								{/* <IconButton aria-label="Voice input" icon={<MicrophoneOutline />} size="sm" variant="ghost" borderRadius="md" /> */}
							</HStack>
						</Box>
						<HStack justify="space-between">
							<Button variant="ghost" colorScheme="primary" size="sm" leftIcon={<PaperClipOutline />} onClick={onAddActivity}>
								Add activity
							</Button>
							<Button
								colorScheme="primary"
								size="sm"
								variant="solid"
								onClick={onSubmitUpdate}
								isLoading={isSubmitting}
								loadingText="Posting..."
								isDisabled={!updateText.trim()}
							>
								Post update
							</Button>
						</HStack>
					</VStack>
				)}

				{/* Timeline */}
				<VStack spacing={4} align="stretch" aria-live="polite" data-component="goal-details-timeline">
					{isHistoryLoading && (
						<>
							<VStack alignItems="left" width="full" gap={4} py={2}>
								<Skeleton width="40%" height="16px" />
								<Skeleton width="100%" height="88px" mb={4} />
								<Skeleton width="40%" height="16px" />
								<Skeleton width="100%" height="48px" mb={4} />
								<Skeleton width="40%" height="16px" />
								<Skeleton width="100%" height="64px" mb={4} />
							</VStack>
						</>
					)}

					{!isHistoryLoading &&
						groupedActivities.map((dateGroup) => (
							<VStack key={dateGroup.date} align="stretch" spacing={3}>
								{/* Date Header */}
								<Text fontSize={50} fontWeight="medium" color="text.softest" mb={-1}>
									{dateGroup.date}
								</Text>

								{/* Activities for this date */}
								{dateGroup.activities.map((activity) => {
									// Handle Growth::GoalUpdate type
									if (activity.type === 'Growth::GoalUpdate') {
										return (
											<MotionBox key={activity.id} variants={timelineEntryVariants} initial="hidden" animate="visible">
												<GoalUpdateCard
													goalUpdate={{
														id: activity.id,
														userId: activity.addedByUserId || activity.userId,
														content: activity.summary || '',
														source: activity.source || 'user',
														createdAt: activity.addedAt || activity.createdAt,
													}}
												/>
											</MotionBox>
										);
									}

									// Handle ConstructiveFeedback::Feedback type (your branch's approach)
									if (activity.type === 'ConstructiveFeedback::Feedback' && 'data' in activity) {
										const feedbackId = activity.id;
										if (feedbackId) {
											return (
												<MotionBox key={activity.id} variants={timelineEntryVariants} initial="hidden" animate="visible">
													<FeedbackCardV2 feedbackId={feedbackId} variant="flat" />
												</MotionBox>
											);
										}
										return null;
									}

									// Handle feedback type (main's approach)
									if (activity.type === 'feedback' && 'data' in activity) {
										return (
											<MotionBox key={activity.id} variants={timelineEntryVariants} initial="hidden" animate="visible">
												<FeedbackCardV2 feedback={(activity as any).data} />
											</MotionBox>
										);
									}

									// Handle Bonus type (your branch's approach)
									if (activity.type === 'Bonus' && 'data' in activity) {
										const bonusId = activity.id;
										if (bonusId) {
											return (
												<MotionBox key={activity.id} variants={timelineEntryVariants} initial="hidden" animate="visible">
													<BonusCardV2 bonusId={bonusId} variant="flat" />
												</MotionBox>
											);
										}
										return null;
									}

									if (activity.type === 'recognition' && 'data' in activity) {
										return (
											<MotionBox key={activity.id} variants={timelineEntryVariants} initial="hidden" animate="visible">
												<BonusCardV2 bonus={(activity as any).data} />
											</MotionBox>
										);
									}

									if (activity.type === 'agenda_topic') {
										return (
											<MotionBox key={activity.id} variants={timelineEntryVariants} initial="hidden" animate="visible">
												<TopicHistoryCard title={activity.title || ''} content={activity.summary || ''} createdAt={activity.createdAt || activity.updatedAt} />
											</MotionBox>
										);
									}

									// Fallback for any other types
									return null;
								})}
							</VStack>
						))}
				</VStack>
			</VStack>
		</VStack>
	);
};

const slideVariants: Variants = {
	hidden: { x: '100%', opacity: 0 },
	visible: {
		x: 0,
		opacity: 1,
		transition: {
			type: 'spring',
			stiffness: 400,
			damping: 40,
			duration: 0.6,
		},
	},
	exit: {
		x: '100%',
		opacity: 0,
		transition: {
			duration: 0.3,
			ease: [0.4, 0.0, 0.2, 1], // Custom cubic-bezier for smooth exit
		},
	},
};

interface GoalDetailsPanelProps extends StyleProps {
	goal: Goal | null;
	isOpen: boolean;
	onClose: () => void;
	readonly?: boolean;
	parentContainer?: 'OneOnOneDrawer' | 'default';
	history?: any[];
	isHistoryLoading?: boolean;
}

export const GoalDetailsPanel: FC<GoalDetailsPanelProps> = ({
	goal,
	isOpen,
	onClose,
	readonly = false,
	parentContainer = 'default',
	history = [],
	isHistoryLoading = false,
	...styleProps
}) => {
	const [updateText, setUpdateText] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [isCollaboratorModalOpen, setIsCollaboratorModalOpen] = useState(false);
	const [isGoalCompletedModalOpen, setIsGoalCompletedModalOpen] = useState(false);
	const [isAddActivityModalOpen, setIsAddActivityModalOpen] = useState(false);
	const [isAddingToOneOnOne, setIsAddingToOneOnOne] = useState(false);
	const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
	const [deletingGoal, setDeletingGoal] = useState<Goal | null>(null);

	const toast = useToast();
	const addGoalUpdate = useAddGoalUpdate();
	const updateGoal = useUpdateGoal();
	const deleteGoalMutation = useDeleteGoal();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
	const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
	const bgColor = useColorModeValue('white', 'gray.800');
	const borderColor = useColorModeValue('border.default', 'gray.600');

	// Calculate navigation offset to account for dynamic headers (like spoofing banner)
	const navWrapper = document.getElementById('nav-wrapper');
	const naviOffset = navWrapper?.offsetHeight || 0;

	// Fetch live goal data to ensure we have the latest activities
	const { data: liveGoal, isLoading: isLoadingGoal } = useGoal(goal?.id || '', {
		enabled: !!goal?.id && isOpen,
	});

	// Use live goal data if available, fallback to prop
	const currentGoal = liveGoal || goal;

	// Check if goal is associated with a 1:1 topic
	const {
		data: topicAssociation,
		isLoading: isLoadingAssociation,
		refetch: refetchAssociation,
	} = useAgendaTopicAssociated({
		entityType: 'Growth::Goal',
		entityId: currentGoal?.id || '',
		enabled: !!currentGoal?.id && isOpen,
	});

	// Mutation for creating topic from entity
	const createTopicFromEntity = useAgendaTopicFromEntity();

	const handleClose = () => {
		setIsClosing(true);
		setUpdateText('');
		// Delay calling the parent's onClose to allow exit animation
		setTimeout(() => {
			setIsClosing(false);
			onClose();
		}, 350); // Slightly longer than animation duration
	};

	const handleSubmitUpdate = async () => {
		if (!updateText.trim() || !currentGoal) return;

		setIsSubmitting(true);
		try {
			await addGoalUpdate.mutateAsync({
				goalId: currentGoal.id,
				activityType: 'goal_update',
				activityData: {
					summary: updateText.trim(),
				},
			});

			setUpdateText('');
			toast({
				title: 'Update posted!',
				status: 'success',
				duration: 3000,
			});
		} catch (error) {
			console.error('Failed to post update:', error);
			toast({
				title: 'Failed to post update',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCompleteGoal = async () => {
		if (!currentGoal) return;

		try {
			await updateGoal.mutateAsync({
				id: currentGoal.id,
				data: {
					status: 'completed',
					completedAt: new Date(),
				},
			});

			toast({
				title: 'Goal completed! 🎉',
				description: 'Congratulations on your achievement!',
				status: 'success',
				duration: 4000,
			});

			// Open celebration modal after successful completion
			setIsGoalCompletedModalOpen(true);
		} catch (error) {
			console.error('Failed to complete goal:', error);
			toast({
				title: 'Failed to complete goal',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		}
	};

	const handleDiscussInOneOnOne = async () => {
		if (!currentGoal || topicAssociation?.associated) return;

		setIsAddingToOneOnOne(true);
		try {
			await createTopicFromEntity.mutateAsync({
				entityType: 'Growth::Goal',
				entityId: currentGoal.id,
			});

			// Refetch the association status to update the UI
			refetchAssociation();

			toast({
				title: 'Added to 1:1 agenda! 🎯',
				description: 'This goal has been added to your next 1:1 meeting.',
				status: 'success',
				duration: 4000,
			});
		} catch (error) {
			console.error('Failed to add goal to 1:1:', error);
			toast({
				title: 'Failed to add to 1:1 agenda',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		} finally {
			setIsAddingToOneOnOne(false);
		}
	};

	const handleCollaboratorModalClose = () => {
		setIsCollaboratorModalOpen(false);
	};

	const handleCollaboratorSuccess = () => {
		// This will trigger a re-render with updated collaborators
		// The parent component should handle refetching the goal data
	};

	const handleGoalCompletedModalClose = () => {
		setIsGoalCompletedModalOpen(false);
	};

	const handleGoalCompletedSuccess = () => {
		// Goal has been completed, this will trigger a re-render
		// The parent component should handle refetching the goal data
	};

	const handleEditGoal = () => {
		if (!currentGoal) return;
		setEditingGoal(currentGoal);
		onEditModalOpen();
	};

	const handleDeleteGoal = () => {
		if (!currentGoal) return;
		setDeletingGoal(currentGoal);
		onDeleteDialogOpen();
	};

	const confirmDeleteGoal = async () => {
		if (!deletingGoal) return;

		try {
			await deleteGoalMutation.mutateAsync(deletingGoal.id);
			onDeleteDialogClose();
			setDeletingGoal(null);
			// Close the details panel after successful deletion
			onClose();
		} catch (error) {
			console.error('Error deleting goal:', error);
		}
	};

	const handleCloseEditModal = () => {
		onEditModalClose();
		setEditingGoal(null);
	};

	const handleCloseDeleteDialog = () => {
		onDeleteDialogClose();
		setDeletingGoal(null);
	};

	const handleStatusChange = async (newStatus: GoalStatus) => {
		if (!currentGoal || newStatus === currentGoal.status) return;
		// save the previous status so we can revert it if the update fails
		const prevStatus = currentGoal.status;
		// update the status in the current goal object so the UI updates immediately
		currentGoal.status = newStatus;
		try {
			await updateGoal.mutateAsync({
				id: currentGoal.id,
				data: {
					status: newStatus,
					...(newStatus === 'completed' && { completedAt: new Date() }),
				},
			});

			toast({
				title: 'Status updated successfully!',
				status: 'success',
				duration: 3000,
			});

			// If goal was completed, show celebration modal
			if (newStatus === 'completed') {
				setIsGoalCompletedModalOpen(true);
			}
		} catch (error) {
			currentGoal.status = prevStatus;
			console.error('Failed to update goal status:', error);
			toast({
				title: 'Failed to update status',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		}
	};

	const formatRelativeDate = (date: Date) => {
		const now = new Date();
		const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

		if (diffInDays === 0) return 'Today';
		if (diffInDays === 1) return 'Yesterday';
		// For anything older than yesterday, show the actual date
		return format(date, 'MMM d, yyyy');
	};

	// Group activities by date
	const groupedActivities = useMemo(() => {
		if (!currentGoal?.activities) return [];

		let groups = currentGoal.activities.reverse().reduce(
			(acc, activity) => {
				// Check if an activity with the same userId and summary already exists
				const isDuplicate = Object.values(acc).some((activities) => activities.some((a) => a.userId === activity.userId && a.summary === activity.summary));
				if (isDuplicate) return acc;

				const activityDate = new Date(activity.addedAt || activity.createdAt);
				const date = formatRelativeDate(activityDate);
				if (!acc[date]) {
					acc[date] = [];
				}
				acc[date].push(activity);
				return acc;
			},
			{} as Record<string, Activity[]>
		);

		groups = history?.reduce((acc, item: any) => {
			const activityDate = new Date(item.date);
			const date = formatRelativeDate(activityDate);
			acc[date] = acc[date] || [];
			acc[date].push({
				id: +new Date() + Math.floor(Math.random() * 1000000), // no real id, just a made up one
				type: 'agenda_topic',
				userId: item.created_by,
				title: item.type,
				summary: item.content,
				createdAt: item.date,
				updatedAt: item.date,
			});
			return acc;
		}, groups);

		// Convert to array and sort by date (most recent first)
		return Object.entries(groups)
			.map(([date, activities]) => ({
				date,
				activities: activities.sort((a, b) => {
					// Sort activities within each date group by newest first
					const aTime = new Date(a.addedAt || a.createdAt).getTime();
					const bTime = new Date(b.addedAt || b.createdAt).getTime();
					return bTime - aTime;
				}),
			}))
			.sort((a, b) => {
				// Sort by the first activity's timestamp in each group
				const aTime = new Date(a.activities[0]?.addedAt || a.activities[0]?.createdAt).getTime() || 0;
				const bTime = new Date(b.activities[0]?.addedAt || b.activities[0]?.createdAt).getTime() || 0;
				return bTime - aTime;
			});
	}, [currentGoal?.activities, history]);

	// Show loading spinner while fetching goal data (only when panel should be open)
	if (isOpen && isLoadingGoal && !currentGoal) {
		return (
			<MotionBox
				position="fixed"
				top={`${naviOffset}px`}
				right={0}
				bottom={0}
				width={{ base: '100vw', md: '480px' }}
				bg={bgColor}
				borderLeft={{ base: 'none', md: '1px solid' }}
				borderColor={borderColor}
				zIndex={1001}
				display="flex"
				alignItems="center"
				justifyContent="center"
			>
				<VStack spacing={4}>
					<Text>Loading goal details...</Text>
				</VStack>
			</MotionBox>
		);
	}

	if (!currentGoal) return null;

	// Render the modals outside of the conditional rendering so they work in both modes
	const modals = (
		<>
			{/* Add Activity Modal */}
			<AddActivityToGoalModal isOpen={isAddActivityModalOpen} onClose={() => setIsAddActivityModalOpen(false)} goalId={currentGoal.id} />

			{/* Goal Collaborator Manager Modal */}
			<GoalCollaboratorManager
				isOpen={isCollaboratorModalOpen}
				onClose={handleCollaboratorModalClose}
				goal={currentGoal}
				onSuccess={handleCollaboratorSuccess}
			/>

			{/* Goal Completed Modal */}
			<GoalCompletedModal isOpen={isGoalCompletedModalOpen} onClose={handleGoalCompletedModalClose} goal={currentGoal} onSuccess={handleGoalCompletedSuccess} />

			{/* Edit Goal Modal */}
			<EditGoalModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} goal={editingGoal} />

			{/* Delete Confirmation Dialog */}
			<AlertDialog isOpen={isDeleteDialogOpen} onClose={handleCloseDeleteDialog} leastDestructiveRef={cancelRef}>
				<AlertDialogOverlay />
				<AlertDialogContent>
					<AlertDialogHeader>Delete Goal</AlertDialogHeader>
					<AlertDialogBody>Are you sure you want to delete "{deletingGoal?.title}"? This action cannot be undone.</AlertDialogBody>
					<AlertDialogFooter>
						<Button ref={cancelRef} onClick={handleCloseDeleteDialog} variant="alpha" colorScheme="secondary">
							Cancel
						</Button>
						<Button
							bg="red.500"
							_active={{
								bg: 'red.700',
							}}
							_hover={{
								bg: 'red.600',
							}}
							onClick={confirmDeleteGoal}
							ml={3}
							isLoading={deleteGoalMutation.isLoading}
							loadingText="Deleting..."
						>
							Delete
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);

	// If in drawer mode, render without overlay and animations
	if (parentContainer === 'OneOnOneDrawer') {
		return (
			<>
				<VStack spacing={4} align="stretch" {...styleProps}>
					<GoalDetailsContent
						goal={currentGoal}
						readonly={readonly}
						updateText={updateText}
						setUpdateText={setUpdateText}
						isSubmitting={isSubmitting}
						onSubmitUpdate={handleSubmitUpdate}
						onStatusChange={handleStatusChange}
						onDiscussInOneOnOne={handleDiscussInOneOnOne}
						onCompleteGoal={handleCompleteGoal}
						onEditGoal={handleEditGoal}
						onDeleteGoal={handleDeleteGoal}
						onAddActivity={() => setIsAddActivityModalOpen(true)}
						topicAssociation={topicAssociation}
						isAddingToOneOnOne={isAddingToOneOnOne}
						isLoadingAssociation={isLoadingAssociation}
						groupedActivities={groupedActivities}
						borderColor={borderColor}
						parentContainer={parentContainer}
						isHistoryLoading={isHistoryLoading}
						toast={toast}
					/>
				</VStack>
				{modals}
			</>
		);
	}

	return (
		<Portal>
			<AnimatePresence mode="wait">
				{(isOpen || isClosing) && (
					<>
						{/* Overlay */}
						<MotionBox
							key="goal-details-overlay"
							position="fixed"
							top={0}
							left={0}
							right={0}
							bottom={0}
							bg="blackAlpha.500"
							zIndex={9}
							initial={{ opacity: 0 }}
							animate={{ opacity: isClosing ? 0 : 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3, ease: 'easeInOut' }}
							onClick={handleClose}
						/>

						{/* Panel */}
						<MotionBox
							key="goal-details-panel"
							position="fixed"
							top={`${naviOffset}px`}
							right={0}
							bottom={0}
							width={{ base: '100vw', md: '480px' }}
							bg={bgColor}
							borderLeft={{ base: 'none', md: '1px solid' }}
							borderColor={borderColor}
							zIndex={9}
							variants={slideVariants}
							initial="hidden"
							animate={isClosing ? 'exit' : 'visible'}
							exit="exit"
							overflowY="auto"
							boxShadow="2xl"
							dropShadow="lg"
							aria-labelledby="goal-details-title"
							aria-describedby="goal-details-content"
							data-component="goal-details-panel"
						>
							<VStack spacing={0} align="stretch" h="full">
								{/* Fixed Header - Only title and close button */}
								<Box py={3} px={4} borderBottom="1px solid" borderColor={borderColor} bg={bgColor} position="sticky" top={0} zIndex={10}>
									<HStack justify="space-between" align="center">
										<Text id="goal-details-title" fontSize="xl" fontWeight="semibold">
											Goal details
										</Text>
										<IconButton aria-label="Close goal details" icon={<XOutline />} variant="ghost" size="sm" onClick={handleClose} />
									</HStack>
								</Box>

								{/* Scrollable Content */}
								<Box flex={1} p={5}>
									<GoalDetailsContent
										goal={currentGoal}
										readonly={readonly}
										updateText={updateText}
										setUpdateText={setUpdateText}
										isSubmitting={isSubmitting}
										onSubmitUpdate={handleSubmitUpdate}
										onStatusChange={handleStatusChange}
										onDiscussInOneOnOne={handleDiscussInOneOnOne}
										onCompleteGoal={handleCompleteGoal}
										onEditGoal={handleEditGoal}
										onDeleteGoal={handleDeleteGoal}
										onAddActivity={() => setIsAddActivityModalOpen(true)}
										topicAssociation={topicAssociation}
										isAddingToOneOnOne={isAddingToOneOnOne}
										isLoadingAssociation={isLoadingAssociation}
										groupedActivities={groupedActivities}
										borderColor={borderColor}
										parentContainer={parentContainer}
										toast={toast}
									/>
								</Box>
							</VStack>
						</MotionBox>
					</>
				)}
			</AnimatePresence>
			{modals}
		</Portal>
	);
};
